import { Alert } from "components/elements";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { message, Modal } from "antd";

import { generateRandomString } from "utils/helper";
import * as services from "../services/employeeApi";
import { setIsLoading, setWorkInLocationList, setWorkInLocationOptions } from "../slices/employeeSlice";
import useOptions from "features/masterData/hooks/useOptions";

function useEmployeeWorkInWorkInLocation() {
  const dispatch = useDispatch();
  const { id } = useParams();
  const isLoading = useSelector((state) => state.employee.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  const currentEmployee = useSelector((state) => state.company.currentEmployee);
  const shiftList = useSelector((state) => state.employee.shiftList);
  const [isModalOpen, setModalOpen] = useState(false);
  const [currentDeviceType, setCurrentDeviceType] = useState();
  const [workInLocationId, setWorkInLocationId] = useState();
  const { deviceTypeOptions = [], getDeviceTypeOptions } = useOptions();
  const [search, setSearch] = useState({});
  const [pagination, setPagination] = useState({ current: 1, page: 1, size: 10 });
  const { workInLocationOptions, workInLocationList } = useSelector(
    (state) => state.employee
  );
  const { t, i18n } = useTranslation();


  function toParams(params = {}) {
    const { pagination = {}, sortBy = [] } = params;
    const modifiedParams = {
      pagination: {
        page: pagination?.page,
        size: pagination?.pageSize,
      },
      sortBy: sortBy
        .filter((s) => s.order !== undefined)
        .map((s) => ({
          direction: s.order === "descend" ? "desc" : "asc",
          property: s.field,
        })),
    };
    return modifiedParams;
  }

  const getWorkInLocationOptions = useCallback(async () => {
    try {
      dispatch(setIsLoading(true));
      const response = await services.getWorkInLocationInEmployee({
        requestId: generateRandomString(),
        companyId: currentCompany,
        employeeId: id,
      });
      const modifiedOptions = response.data?.map(opt =>
        workInLocationList.includes(opt.id) ? null : {
          ...opt,
          label: opt.name,
          value: opt.id,
        }
      ).filter(opt => opt !== null);
      setWorkInLocationId(modifiedOptions[0]?.value);
      dispatch(setWorkInLocationOptions(modifiedOptions));
    } catch (error) {
    } finally {
      dispatch(setIsLoading(false));
    }
  }, [currentCompany, id]);

  const getWorkInLocationPaging = useCallback(
    async (params = {}) => {

      try {
        dispatch(setIsLoading(true));
        const response = await services.getWorkInLocation({
          ...params,
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: id,
          actionId: currentEmployee,
          deviceTypeId: currentDeviceType,
          sortBy: params.sortBy,
        });
        dispatch(setWorkInLocationList(response?.data?.workInLocation || []));
        getWorkInLocationOptions();
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentDeviceType, currentEmployee, dispatch, getWorkInLocationOptions, id]
  );

  const createWorkInLocation = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.createWorkInLocation({
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: id,
          actionId: currentEmployee,
          workInLocationId: params?.workInLocationId,
        });
        getWorkInLocationPaging();
        setModalOpen(false);
        setWorkInLocationId(null);
        if (response?.data?.status?.details[0]?.key === "ErrorTH") {
          Alert({
            type: "error", message: i18n.language === "en" ?
              response?.data?.status?.details[1]?.value :
              response?.data?.status?.details[0]?.value
          });
        } else {
          message.success(t("common.success"))
        }
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch, getWorkInLocationPaging, i18n.language, id]
  );

  const deleteWorkInLocation = useCallback(
    async (params = {}) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.deleteWorkInLocation({
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId: id,
          actionId: currentEmployee,
          workInLocationId: params?.id,
        });
        getWorkInLocationPaging();
        setModalOpen(false);
        setWorkInLocationId(null);
        if (response?.data?.status?.details[0]?.key === "ErrorTH") {
          Alert({
            type: "error", message: i18n.language === "en" ?
              response?.data?.status?.details[1]?.value :
              response?.data?.status?.details[0]?.value
          });
        } else {
          message.success(t("common.success"))
        }
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, currentEmployee, dispatch, getWorkInLocationPaging, i18n.language, id]
  );

  const onChange = useCallback(
    (tablePagination, tableFilters, tableSorter, tableExtra) => {
      const modifiedSorter = Array.isArray(tableSorter)
        ? [...tableSorter]
        : [tableSorter];

      // if (modifiedSorter.filter((s) => s.order !== undefined).length == 0) {
      //   modifiedSorter.push({
      //     order: 'asc',
      //     field: 'createDate',
      //   })
      // }
      const modifiedParams = toParams({
        pagination: {
          page: tablePagination.current - 1,
          pageSize: tablePagination.pageSize,
          current: tablePagination.current - 1,
        },
        sortBy: modifiedSorter,
      });
      console.log('modifiedParams:', modifiedParams);

      setPagination({ ...pagination, ...modifiedParams?.pagination })
      getWorkInLocationPaging({ ...modifiedParams, request: pagination.request });
    },
    [getWorkInLocationPaging, pagination]
  );

  const onSubmit = useCallback(
    (values) => {
      const modifiedValues = { ...values };
      createWorkInLocation(modifiedValues);
    },
    [createWorkInLocation]
  );

  const onDelete = useCallback(
    (values) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to delete ?</span>
          : <span>คุณยืนยันการลบใช่หรือไม่ ?</span>
        ),
        onOk() {
          const modifiedValues = { ...values };
          deleteWorkInLocation(modifiedValues);
        },
        okText: t("common.confirm").toUpperCase(),
        cancelText: t("common.cancel").toUpperCase(),
      })
    }, [deleteWorkInLocation]);

  const onCancel = useCallback(() => {
    setModalOpen(false);
  }, []);

  const onCreate = useCallback(() => {
    setModalOpen(true);
  }, []);

  const modifiedDeviceTypeOptions = useMemo(() => {
    const modifiedOptions = deviceTypeOptions?.map((opt) => ({
      ...opt,
      label: i18n.language === "en" ? opt.nameEn : opt.name,
      value: opt.id,
    }));
    const allOption = {
      label: i18n.language === "en" ? "All Device Type" : "ประเภทอุปกรณ์ทั้งหมด",
      value: "",
    };
    return [allOption, ...modifiedOptions];
  }, [deviceTypeOptions, i18n.language]);

  const onDeviceTypeChange = (value) => {
    setCurrentDeviceType(value);
  };

  useEffect(() => {
    if (id && currentCompany) {
      getWorkInLocationPaging();
    }
  }, [currentCompany, getWorkInLocationPaging, id,]);

  useEffect(() => {
    getDeviceTypeOptions();
  }, [getDeviceTypeOptions]);

  useEffect(() => {
    getWorkInLocationOptions();
  }, [getWorkInLocationOptions]);

  return {
    isLoading,
    shiftList,
    isModalOpen,
    workInLocationId,
    workInLocationList,
    workInLocationOptions,
    deviceTypeOptions: modifiedDeviceTypeOptions,
    currentDeviceType,
    onDeviceTypeChange,
    onCreate,
    onCancel,
    onSubmit,
    onDelete,
    onChange,
  };
}

export default useEmployeeWorkInWorkInLocation;